import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import message from 'antd/lib/message';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { FirebaseContext } from '../../context/FirebaseContext';
import TimeSheduleClassCard from '../../components/TimeSheduleClassCard';
import Button from '../../NecttosComp/Button/Button';
import { OLoading } from '../../static/svg/icons';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import Instruction from '../../Documentation/Instruction';

const getPeriodsList = async ({ queryKey }) => {
  const [_, { collegeId }] = queryKey;
  const instance = await getAxiosTokenInstance();
  let resp;
  if (collegeId) {
    try {
      resp = await instance.get('college/timetable/periods', { params: { collegeId } });
      if (resp.data.statusCode === 400) throw new Error(resp.data.message || 'API Error');
    } catch (error) {
      throw new Error(error?.response?.data?.message || error.message || 'API Error');
    }
  }
  return resp?.data;
};

const updatePeriodsList = async ({ data }) => {
  data?.classes?.forEach(e => {
    delete e.collegeId;
  });
  const instance = await getAxiosTokenInstance();
  const resp = await instance.post('/college/timetable/periods', data);
  if (resp.data.statusCode === 400) throw new Error(resp.data.message || 'API Error');
  return resp.data;
};

function TimeShedulePage({ onClose }) {
  const [masterTemplate, setMasterTemplate] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [timmerHide, setTimmerHide] = useState(false);
  const { collegeId } = useContext(FirebaseContext);

  const periodsQuery = useQuery(['periods', { collegeId }], getPeriodsList);

  const { mutate, isLoading } = useMutation(updatePeriodsList, {
    onSuccess: (data) => {
      periodsQuery.refetch();
      message.success(data.message);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    if (periodsQuery.data?.list && periodsQuery.data.list.length > 0) {
      // Store all classes for database update
      setAllClasses(periodsQuery.data.list);
      
      // Use the first class as the master template for display
      const firstClass = periodsQuery.data.list[0];
      setMasterTemplate({
        ...firstClass,
        className: 'All Classes' // Generic name since it represents all classes
      });
    } else {
      // Default template if no data
      setMasterTemplate({
        classId: 'master',
        className: 'All Classes',
        periods: []
      });
      setAllClasses([]);
    }
  }, [periodsQuery.data]);

  const setPeriodValue = (periodIndex, field, value) => {
    setMasterTemplate(prev => {
      if (!prev) return prev;
      
      const updatedPeriods = [...(prev.periods || [])];
      
      if (!updatedPeriods[periodIndex]) {
        updatedPeriods[periodIndex] = { startTime: '', endTime: '' };
      }
      
      updatedPeriods[periodIndex] = {
        ...updatedPeriods[periodIndex],
        [field]: value,
      };
      
      return {
        ...prev,
        periods: updatedPeriods
      };
    });
  };

  const setCountValue = (count) => {
    setMasterTemplate(prev => {
      if (!prev) return prev;
      
      const currentPeriods = prev.periods || [];
      const newCount = parseInt(count) || 0;
      let newPeriods;

      if (newCount < currentPeriods.length) {
        newPeriods = currentPeriods.slice(0, newCount);
      } else if (newCount > currentPeriods.length) {
        newPeriods = [
          ...currentPeriods,
          ...Array(newCount - currentPeriods.length).fill({ startTime: '', endTime: '' }),
        ];
      } else {
        newPeriods = currentPeriods;
      }

      return {
        ...prev,
        periods: newPeriods
      };
    });
  };

  const handleSubmit = () => {
    if (!masterTemplate) return;
    
    // Filter out empty periods (periods without both startTime and endTime)
    const validPeriods = masterTemplate.periods.filter(period => 
      period.startTime && period.startTime.trim() !== '' && 
      period.endTime && period.endTime.trim() !== ''
    );
    
    // Apply the filtered periods to all classes
    const updatedClasses = allClasses.map(cls => ({
      ...cls,
      periods: validPeriods
    }));

    const data = { collegeId, classes: updatedClasses };
    mutate({ data });
  };

  return (
    <>
      {isLoading && (
        <div className='fixed inset-0 bg-slate-100 dark:bg-zinc-950 bg-opacity-20 flex items-center justify-center z-[9999]'>
          <OLoading />
        </div>
      )}
      <Main title='Period Management' height='97%' width='97%'>
        <Header>
          <div className='flex justify-end w-full'>
            <Instruction keyId={'timetable'}></Instruction>
            <Button type='close' onClick={onClose}>Close</Button>
          </div>
        </Header>
        <Body>
          <div className="text-center text-[36px] dark:text-slate-100 font-bold">
            Period Management
          </div>
          
          <div className="text-center text-lg dark:text-slate-200 mb-4">
            Configure periods for all classes ({allClasses.length} classes will be updated)
          </div>

          <div className='flex justify-center'>
            {periodsQuery.isLoading ? (
              <OLoading />
            ) : masterTemplate ? (
              <TimeSheduleClassCard
                key={masterTemplate.classId}
                data={masterTemplate}
                index={0}
                pos="master"
                setTimmerHide={setTimmerHide}
                setCountValue={setCountValue}
                setPeriodValue={setPeriodValue}
              />
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400">
                No data available
              </div>
            )}
          </div>
          
          <div className='flex justify-end'>
            <Button type='update' onClick={() => (!timmerHide ? handleSubmit() : null)}>
              Update All Classes
            </Button>
          </div>
        </Body>
      </Main>
    </>
  );
}
export default TimeShedulePage;